import React, {useEffect, useState} from "react";
import {AppBar, Avatar, Box, Button, IconButton, Menu, MenuItem, Toolbar, Tooltip, useMediaQuery, useTheme,} from "@mui/material";
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {HOME_URL, LOGIN_URL, PROFILE_URL,} from "../../utils/urls";
import {ROLE_MAP, tryGoToDefaultPage, useAuthManager, useHasAnyRole} from "../../utils/auth";
import {COLOR_VERDE_ARTECO} from "../colors";
import {Link, useLocation, useNavigate} from "react-router-dom";
import LogoutIcon from '@mui/icons-material/Logout';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import {UserAuthItemTypeEnum} from "arteco-api-client-ts";
import NavbarItem from "./navbar_item";
import {envVars} from "../../utils/env_vars";
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentIcon from '@mui/icons-material/Assignment';
import {ImpersonationBanner, ImpersonationDialog, useImpersonation} from "./impersonation";

// Orden de las secciones en la barra.
const ROLES: UserAuthItemTypeEnum[] = [
    UserAuthItemTypeEnum.ADMIN_BILLING,
    UserAuthItemTypeEnum.ADMIN_ACCOUNTING,
    UserAuthItemTypeEnum.ADMIN_MARKETING,
    UserAuthItemTypeEnum.ADMIN_EMPLOYEE,
    UserAuthItemTypeEnum.ADMIN_RECRUITMENT,
    UserAuthItemTypeEnum.ADMIN_COMPLIANCE,
    UserAuthItemTypeEnum.ADMIN,
    UserAuthItemTypeEnum.EMPLOYEE,
];

// Las tareas viven en Redmine, no en el portal (#14096): abiertas y asignadas a quien entra.
const REDMINE_MY_ISSUES = "https://redmine.artecoapps.com/issues?set_filter=1&assigned_to_id=me&status_id=o&sort=priority:desc,updated_on:desc";

const Navbar = () => {
    const basePath = envVars.apiBasePath;
    const authManager = useAuthManager();
    const [menuElementReference, setMenuElementReference] = useState<any | undefined>();

    const showUserMenu = Boolean(menuElementReference);
    const impersonation = useImpersonation();
    const isEmployee = useHasAnyRole(UserAuthItemTypeEnum.EMPLOYEE);
    // el buscador es de la administración (#14123): cualquier rol ADMIN_*, cada uno ve lo suyo
    const canSearch = useHasAnyRole(...ROLES.filter(r => r !== UserAuthItemTypeEnum.EMPLOYEE));
    const [impersonationOpen, setImpersonationOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    // En móvil las siete secciones no caben en línea y la página ganaba scroll horizontal (#13825):
    // pasan a un desplegable con la sección actual.
    const compact = useMediaQuery(theme.breakpoints.down("md"));
    const [sectionsAnchor, setSectionsAnchor] = useState<HTMLElement | null>(null);

    useEffect(() => {
        const auths = authManager.getAuthorities();
        if (!authManager.getSelectedAuthority() && auths && auths.length > 0) {
            const auth = auths[0];
            tryGoToDefaultPage(auth, authManager, navigate);
        }
    }, []);

    const showPopover = (event: any) => {
        setMenuElementReference(event.currentTarget);
    };

    return (
        <>
            <Box sx={{flexGrow: 1}}>
                <AppBar position="static"
                        style={{backgroundColor: basePath && basePath.indexOf("arteco-consulting.com") > -1 ? "undefined" : "#4d0707"}}>
                    <Toolbar>

                        <Link to={HOME_URL} style={{marginRight: compact ? "0.5em" : "2em", flexShrink: 0}}>
                            <img width={compact ? 88 : 112} src="/img/logo_verde.png" alt="Arteco logo"/>
                        </Link>


                        {authManager.isLogged() && !compact && (
                            <Box sx={{flexGrow: 1, mx: 2, overflow: "auto", minWidth: 0}}>
                                {ROLES.map(role => <NavbarItem key={role} map={ROLE_MAP} role={role}/>)}
                            </Box>
                        )}

                        {authManager.isLogged() && compact && (
                            <Box sx={{flexGrow: 1, mx: 1, minWidth: 0}}>
                                <Button color="inherit" endIcon={<ExpandMoreIcon/>}
                                        onClick={e => setSectionsAnchor(e.currentTarget)}
                                        sx={{maxWidth: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                    {ROLES.map(role => ROLE_MAP.get(role))
                                        .find(info => info?.target && location.pathname.startsWith(info.target))?.title || "Secciones"}
                                </Button>
                                <Menu open={Boolean(sectionsAnchor)}
                                      anchorEl={sectionsAnchor}
                                      onClose={() => setSectionsAnchor(null)}>
                                    {ROLES.map(role => (
                                        <NavbarItem key={role} map={ROLE_MAP} role={role} asMenuItem
                                                    onSelect={() => setSectionsAnchor(null)}/>
                                    ))}
                                </Menu>
                            </Box>
                        )}


                        {authManager.isLogged() && (

                            <>
                                {canSearch && (
                                    <Tooltip title="Buscar">
                                        <IconButton color="inherit" aria-label="Buscar" sx={{mr: 1, flexShrink: 0}}
                                                    onClick={() => navigate("/app/admin/search")}>
                                            <SearchRoundedIcon/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Avatar
                                    onClick={showPopover}
                                    sx={{flexShrink: 0}}
                                    // el alt va a la imagen y no al Avatar: si no hay foto, MUI pondría la inicial en vez de la silueta
                                    slotProps={{img: {alt: authManager.getUsername()}}}
                                    src={authManager.getImageSrc()}
                                    style={{
                                        cursor: "pointer",
                                        border: `2px solid ${COLOR_VERDE_ARTECO} `,
                                    }}
                                />
                                <Menu
                                    open={showUserMenu}
                                    onClose={() => setMenuElementReference(null)}
                                    anchorEl={menuElementReference}>
                                    <MenuItem onClick={() => {
                                        navigate(PROFILE_URL);
                                    }}>
                                        <AccountBoxIcon sx={{mx: 1}}/> Perfil [{authManager.getUsername()}]
                                    </MenuItem>
                                    {isEmployee && (
                                        <MenuItem component="a" href={REDMINE_MY_ISSUES} target="_blank" rel="noreferrer"
                                                  onClick={() => setMenuElementReference(null)}>
                                            <AssignmentIcon sx={{mx: 1}}/> Mis tareas en Redmine
                                        </MenuItem>
                                    )}
                                    {impersonation.canStart && (
                                        <MenuItem onClick={() => {
                                            setMenuElementReference(null);
                                            setImpersonationOpen(true);
                                        }}>
                                            <VisibilityIcon sx={{mx: 1}}/> Ver como…
                                        </MenuItem>
                                    )}
                                    <MenuItem onClick={() => authManager.doLogout()}>
                                        <LogoutIcon sx={{mx: 1}}/> Salir
                                    </MenuItem>
                                </Menu>
                            </>

                        )}
                        {!authManager.isLogged() && (
                            <Button onClick={() => navigate(LOGIN_URL)}>Login</Button>
                        )}

                    </Toolbar>
                </AppBar>
                {impersonation.impersonator && (
                    <ImpersonationBanner username={authManager.getUsername()}
                                         impersonator={impersonation.impersonator}
                                         onExit={impersonation.exit}/>
                )}
                <ImpersonationDialog open={impersonationOpen}
                                     onClose={() => setImpersonationOpen(false)}
                                     onStart={impersonation.start}/>
            </Box>

        </>
    );
};

export default Navbar;
